/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from 'react'
import GenericEditScreen, {
  RowContainer,
  RowTitle,
} from 'core/component/GenericEditScreen'
import { Filler } from 'core/screen-util'
import { Button, Popconfirm, Avatar } from 'antd'
import { connect } from 'react-redux'
import R, { sortWith, ascend } from 'ramda'
import UserModal, { PasswordResetModal } from './userModal'
import { getRoles, updateUsers, getUsers, deleteUsers } from 'api/pgads'
import {
  getGroups,
  updateUserPasswords,
} from '@integec/common-react-screens/lib/auth/auth-api'
import {
  getApiTargetInfo,
  getCompanyNameMap,
  getStationsNameMap,
  getEnvConfig,
  getLdapUserNameEnabled,
} from 'core/selectors'

import { ErrorContext, SpinContext } from 'context'
import { dateTimeCol, strCol } from '@integec/grid-tools/lib/cols'
import { getIsPasswordDisabled } from 'utils'

const sortUsers = sortWith([
  ascend(user =>
    `${user.firstName} ${user.lastName} ${user.userName}`.toLowerCase()
  ),
])
const acceptFilter = user => filterText =>
  filterText == null ||
  `${user.firstName} ${user.lastName} ${user.userName}`
    .toLowerCase()
    .includes(filterText.toLowerCase())

const Users = ({ apiTargetInfo, isLdapUsernameEnabled, ...props }) => {
  const [roles, setRoles] = useState([])
  const [groups, setGroups] = useState([])

  const [resetModalVisible, setResetModalVisible] = useState(false)
  const [passwordResetUser, setPasswordResetUser] = useState({})
  const { setError } = useContext(ErrorContext)
  // stationMap
  // const [err, setErr] = useState()
  const { setSpinning, spinning } = useContext(SpinContext)

  const stationList = R.compose(
    R.map(([key, text]) => ({ key, text, value: key })),
    R.sortBy(([_, text]) => text.toLowerCase()),
    R.toPairs
  )(props.stationMap)

  useEffect(() => {
    getGroups(apiTargetInfo)().fork(setError, setGroups)
    getRoles(apiTargetInfo)().fork(setError, setRoles)
  }, [apiTargetInfo, setError])

  const reActivateUser = (user, updateTask) => {
    // if (user.roles && user.roles.length) {
    updateTask({ ...user, userStatus: 'ACTIVE', failedAttempts: 0 }).fork(
      setError,
      _ => _
    )
    // } else {
    //   message.info('Please Assign some roles before Re-Activating User')
    // }
  }

  const revokeUser = (user, updateTask) => {
    updateTask({ ...user, userStatus: 'REVOKED' }).fork(setError, _ => _)
  }

  const renderUserModal = ({ hideModal, modalInfo, list, ...rest }) => (
    <UserModal
      hide={_ => {
        hideModal()
        setSpinning(false)
      }}
      stationMap={props.stationMap}
      user={modalInfo}
      allRoles={roles}
      userList={list}
      groups={groups}
      companyNameMap={props.companyNameMap}
      stationList={stationList}
      setError={setError}
      setSpinning={setSpinning}
      spinning={spinning}
      isLdapUsernameEnabled={isLdapUsernameEnabled}
      {...rest}
    />
  )

  const renderRow = ({
    index,
    list,
    style,
    popModal,
    onDelete,
    updateTask,
  }) => {
    const user = list[index]
    return (
      <RowContainer key={user.userName} style={style}>
        <Avatar
          size="large"
          gap={10}
          style={{
            marginRight: '15px',
            backgroundColor: user.userType === 'ADMIN' ? '#00a2ae' : undefined,
          }}
        >
          {user.userType === 'ADMIN' ? 'A' : 'U'}
        </Avatar>
        <RowTitle onClick={() => popModal(user)}>
          {user.firstName != null
            ? `${user.firstName} ${user.lastName || ''} (${user.userName})`
            : user.userName}
          {/* <div css={{ fontSize: '10pt' }}>@{user.userName}</div> */}
        </RowTitle>

        <Filler />

        <Button.Group>
          {user.userStatus === 'REVOKED' && (
            <Button onClick={() => reActivateUser(user, updateTask)}>
              Re-Activate
            </Button>
          )}

          {user.userStatus === 'LOCKED' && (
            <Button onClick={() => reActivateUser(user, updateTask)}>
              Unlock
            </Button>
          )}
          <Button onClick={() => popModal(user)}>Edit</Button>

          {user.userStatus === 'ACTIVE' && (
            <Popconfirm
              title="Are you sure?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => revokeUser(user, updateTask)}
              placement="topRight"
              // disabled
            >
              <Button>Revoke</Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="Are you sure?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => onDelete(user)}
            placement="topRight"
            // disabled
          >
            <Button>Remove</Button>
          </Popconfirm>
          {!passwordDisabled && (
            <Button onClick={() => showPasswordResetModal(user)}>
              Reset Password
            </Button>
          )}
        </Button.Group>
      </RowContainer>
    )
  }

  const showPasswordResetModal = user => {
    setPasswordResetUser(user)
    setResetModalVisible(true)
  }

  const retrieveTask = useCallback(
    targetInfo => _ => {
      setSpinning(true)
      return getUsers(targetInfo)().map(data => {
        setSpinning(false)
        getRoles(targetInfo)().fork(setError, setRoles)
        return data
      })
    },
    [setError, setSpinning]
  )

  const passwordDisabled = useMemo(
    _ => getIsPasswordDisabled(props.envConfig),
    [props.envConfig]
  )

  return (
    <React.Fragment>
      <GenericEditScreen
        exportHeaders={exportHeaders}
        isExportAvaliable
        title="Users"
        loadApiTask={retrieveTask}
        createApiTask={updateUsers}
        updateApiTask={updateUsers}
        deleteApiTask={deleteUsers}
        renderModal={renderUserModal}
        renderRow={renderRow}
        rowHeight={50}
        sort={sortUsers}
        acceptFilter={acceptFilter}
        dataMatcher={d1 => d2 =>
          d1 !== undefined && d2 !== undefined && d1.userName === d2.userName}
      />
      {resetModalVisible && (
        <PasswordResetModal
          hide={() => setResetModalVisible(false)}
          user={passwordResetUser}
          resetPassword={updateUserPasswords(apiTargetInfo)}
          setError={setError}
        />
      )}
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  companyNameMap: getCompanyNameMap(state),
  stationMap: getStationsNameMap(state),
  envConfig: getEnvConfig(state),
  isLdapUsernameEnabled: getLdapUserNameEnabled(state),
})

export default connect(mapStateToProps)(Users)

const exportHeaders = [
  strCol({
    ident: 'userName',
    display: 'User Name',
  }),
  strCol({
    ident: 'firstName',
    display: 'First Name',
  }),
  strCol({
    ident: 'lastName',
    display: 'Last Name',
  }),
  strCol({
    ident: 'userType',
    display: 'User Type',
  }),
  strCol({
    ident: 'email',
    display: 'Email',
  }),
  strCol({
    ident: 'phone',
    display: 'Phone',
  }),
  strCol({
    ident: 'userStatus',
    display: 'User Status',
  }),
  dateTimeCol({
    ident: 'passwordExpirationTime',
    display: 'Password Expiration Time',
  }),
  dateTimeCol({
    ident: 'lastExitTime',
    display: 'Last Exit Time',
  }),
]
